import React from "react";
import { motion } from "framer-motion";
import {
  Check,
  Users,
  Wrench,
  Sparkles,
  Phone,
  Mail,
  FileText,
  CreditCard,
  ShieldCheck,
  BarChart3,
  Briefcase,
} from "lucide-react";

const frontOfficeAgents = [
  "Amy — Receptionist",
  "Leena — Schedule Optimizer",
  "Stephanie — Hygiene Recall",
  "Kim — Case Acceptance",
  "Elise — Patient Experience",
  "Morgan — Patient Intake",
  "Trisha — Practice Manager",
  "Joseph — Fax Manager",
];

const communicationSuite = [
  "VoIP Phone System",
  "2-Way Messaging",
  "Intelligent Fax",
  "Next-Gen Forms",
  "Team Chat",
  "Online Scheduling",
  "Physical Phones",
];

const marketingSuite = [
  "SMS Campaigns",
  "Email Campaigns",
  "Review Generation",
  "Patient Recalls",
  "Website & SEO",
  "Marketing Analytics",
];

const backOfficeAgents = [
  "Amanda — Insurance Verification",
  "Ariel — Claim Submission",
  "Alayna — Claim Scrubbing",
  "Sarah — Denial Management",
  "Alisha — EOB Posting",
  "Kianna — Patient Billing",
  "Julia — AR & Collections",
  "Laura — Insurance Calls",
];

const revenueIntelligenceSuite = [
  "Revenue Analytics Dashboard",
  "Daily Huddles Dashboard",
  "Collections Reporting",
  "Production Tracking",
  "Provider Performance",
  "AR Aging Reports",
];

const integratedPaymentsSuite = [
  "Card Readers",
  "Text-to-Pay",
  "Payment Processing",
  "Patient Payment Plans",
];

const claimsPortal = [
  "Unified Claim Submission",
  "Real-time Claim Status",
  "Batch Claim Processing",
];

const insuranceVerification = [
  "Real-time Benefits Check",
  "Comprehensive Breakdowns",
  "Eligibility Monitoring",
];

const FeatureSection = ({ title, items, icon: Icon, color }) => (
  <div>
    <div className="flex items-center gap-2 mb-2.5">
      <div className={`w-6 h-6 rounded-md ${color.bg} flex items-center justify-center`}>
        <Icon className={`w-3.5 h-3.5 ${color.icon}`} />
      </div>
      <p className="text-[12.5px] font-bold text-slate-500 uppercase tracking-wider">
        {title}
      </p>
    </div>
    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-2">
          <Check className={`w-3.5 h-3.5 ${color.check} shrink-0 mt-[3px]`} />
          <span className="text-[13.5px] text-slate-700 leading-snug">{item}</span>
        </div>
      ))}
    </div>
  </div>
);

const WhatYouGetSlide = () => {
  return (
    <div className="h-full w-full bg-[#f8fafc] flex flex-col items-center justify-center p-6 xl:p-10 relative overflow-hidden font-sans">
      {/* Ambient gradient blurs */}
      <div
        className="absolute top-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-30 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #bfdbfe 0%, #93c5fd 50%, #dbeafe 100%)" }}
      />
      <div
        className="absolute bottom-[-200px] left-[-100px] w-[600px] h-[600px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #c4b5fd 0%, #a5b4fc 50%, #ddd6fe 100%)" }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="w-full flex flex-col items-center relative z-10 max-w-[1200px]">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6"
        >
          <h2 className="text-3xl lg:text-[42px] font-bold text-slate-900 tracking-tight leading-tight">
            Here's exactly{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              what you get.
            </span>
          </h2>
        </motion.div>

        {/* Side-by-side bundles */}
        <div className="w-full grid grid-cols-2 gap-5">
          {/* FRONT OFFICE AI BUNDLE */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.5 }}
            className="relative rounded-[20px] overflow-hidden border border-white/80 bg-white/95 shadow-[0_20px_60px_-20px_rgba(59,130,246,0.2)]"
          >
            <div
              className="h-[4px]"
              style={{ background: "linear-gradient(90deg, #3b82f6 0%, #6366f1 100%)" }}
            />
            <div className="px-6 pt-6 pb-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-[10.5px] font-bold text-blue-500 uppercase tracking-wider">
                    Bundle 1
                  </p>
                  <h3 className="text-[20px] font-bold text-slate-900 leading-tight">
                    Front Office AI Bundle
                  </h3>
                </div>
              </div>

              <div className="space-y-4">
                <FeatureSection
                  title="8 Front Office AI Agents"
                  items={frontOfficeAgents}
                  icon={Users}
                  color={{
                    bg: "bg-blue-100",
                    icon: "text-blue-600",
                    check: "text-blue-500",
                  }}
                />
                <FeatureSection
                  title="Communication Suite"
                  items={communicationSuite}
                  icon={Phone}
                  color={{
                    bg: "bg-sky-100",
                    icon: "text-sky-600",
                    check: "text-sky-500",
                  }}
                />
                <FeatureSection
                  title="Marketing Suite"
                  items={marketingSuite}
                  icon={Mail}
                  color={{
                    bg: "bg-indigo-100",
                    icon: "text-indigo-600",
                    check: "text-indigo-500",
                  }}
                />
              </div>
            </div>
          </motion.div>

          {/* BACK OFFICE AI BUNDLE */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="relative rounded-[20px] overflow-hidden border border-white/80 bg-white/95 shadow-[0_20px_60px_-20px_rgba(99,102,241,0.2)]"
          >
            <div
              className="h-[4px]"
              style={{ background: "linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)" }}
            />
            <div className="px-6 pt-6 pb-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                  <Briefcase className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-[10.5px] font-bold text-indigo-500 uppercase tracking-wider">
                    Bundle 2
                  </p>
                  <h3 className="text-[20px] font-bold text-slate-900 leading-tight">
                    Back Office AI Bundle
                  </h3>
                </div>
              </div>

              <div className="space-y-4">
                <FeatureSection
                  title="8 Back Office AI Agents"
                  items={backOfficeAgents}
                  icon={Users}
                  color={{
                    bg: "bg-indigo-100",
                    icon: "text-indigo-600",
                    check: "text-indigo-500",
                  }}
                />
                <FeatureSection
                  title="Revenue Intelligence Suite"
                  items={revenueIntelligenceSuite}
                  icon={BarChart3}
                  color={{
                    bg: "bg-violet-100",
                    icon: "text-violet-600",
                    check: "text-violet-500",
                  }}
                />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2.5">
                      <div className="w-6 h-6 rounded-md bg-purple-100 flex items-center justify-center">
                        <CreditCard className="w-3.5 h-3.5 text-purple-600" />
                      </div>
                      <p className="text-[12.5px] font-bold text-slate-500 uppercase tracking-wider leading-tight">
                        Integrated Payments
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      {integratedPaymentsSuite.map((item, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <Check className="w-3.5 h-3.5 text-purple-500 shrink-0 mt-[3px]" />
                          <span className="text-[13.5px] text-slate-700 leading-snug">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2.5">
                      <div className="w-6 h-6 rounded-md bg-fuchsia-100 flex items-center justify-center">
                        <FileText className="w-3.5 h-3.5 text-fuchsia-600" />
                      </div>
                      <p className="text-[12.5px] font-bold text-slate-500 uppercase tracking-wider leading-tight">
                        Claims Portal
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      {claimsPortal.map((item, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <Check className="w-3.5 h-3.5 text-fuchsia-500 shrink-0 mt-[3px]" />
                          <span className="text-[13.5px] text-slate-700 leading-snug">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2.5">
                    <div className="w-6 h-6 rounded-md bg-emerald-100 flex items-center justify-center">
                      <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                    </div>
                    <p className="text-[12.5px] font-bold text-slate-500 uppercase tracking-wider">
                      Insurance Verification Software
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                    {insuranceVerification.map((item, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-[3px]" />
                        <span className="text-[13.5px] text-slate-700 leading-snug">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

      </div>
    </div>
  );
};

export default WhatYouGetSlide;
